/**
 * Utility functions for generating door-based reports from form entries
 * Groups invoice positions by door number for technician view
 */

export interface IDoorReportItem {
    nr: number;
    title: string;
    description: string;
    qty: number;
    unit: string;
    price: number;
    inventoryItemId: number | null;
}

export interface IDoorGroup {
    doorNumber: number;
    doorLabel: string;
    items: IDoorReportItem[];
}

export interface IDoorReport {
    formEntryId: number;
    formEntryName: string;
    entityName: string;
    doors: IDoorGroup[];
    unassignedItems: IDoorReportItem[];
    totalItems: number;
}

interface IInvoicePosition {
    nr: number;
    title: string;
    description: string;
    qty: number;
    unit: string;
    price: number;
    inventory_item_id: number | null;
}

interface IInvoicePositionsData {
    invoicePositions: IInvoicePosition[];
    discount?: number;
    vat?: { value: number; label: string };
    shippingInCents?: number;
    notice?: string;
    notice2?: string;
    processDateBegin?: string;
    processDateEnd?: string | null;
    processDateDelimiter?: string;
    processWorkers?: any[];
    unitSelection?: { label: string; value: string };
}

/**
 * Decodes HTML entities in a string
 * @param text The text containing HTML entities
 * @returns Decoded text
 */
export function decodeHtmlEntities(text: string): string {
    if (!text || typeof text !== "string") {
        return text;
    }

    const entities: { [key: string]: string } = {
        "&uuml;": "ü",
        "&Uuml;": "Ü",
        "&auml;": "ä",
        "&Auml;": "Ä",
        "&ouml;": "ö",
        "&Ouml;": "Ö",
        "&szlig;": "ß",
        "&nbsp;": " ",
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&#39;": "'",
    };

    let decoded = text;
    for (const [entity, char] of Object.entries(entities)) {
        decoded = decoded.replace(new RegExp(entity, "g"), char);
    }

    // Also handle numeric entities like &#252; -> ü
    decoded = decoded.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)));
    decoded = decoded.replace(/&#x([0-9a-fA-F]+);/g, (_, code) =>
        String.fromCharCode(parseInt(code, 16))
    );

    return decoded;
}

/**
 * Extracts door numbers from a description string
 * Looks for patterns like "(Tür 1, Tür 3)" or "Tür 1" or "(Tür 1)"
 * @param description The description text to parse
 * @returns Array of door numbers found
 */
export function extractDoorNumbers(description: string): number[] {
    if (!description || typeof description !== "string") {
        return [];
    }

    // Decode HTML entities first (e.g., &uuml; -> ü)
    const decodedDescription = decodeHtmlEntities(description);

    const doorNumbers: number[] = [];

    // Pattern to match "Tür X" or "Tür X, Tür Y" within parentheses or standalone
    // Matches: "Tür 1", "Tür 12", "(Tür 1)", "(Tür 1, Tür 2)", etc.
    const turPattern = /Tür\s*(\d+)/gi;
    let match;

    while ((match = turPattern.exec(decodedDescription)) !== null) {
        const doorNumber = parseInt(match[1], 10);
        if (!isNaN(doorNumber) && !doorNumbers.includes(doorNumber)) {
            doorNumbers.push(doorNumber);
        }
    }

    return doorNumbers.sort((a, b) => a - b);
}

/**
 * Finds InvoicePositionsListField data in form entry data
 * @param formData The form entry's form_data object
 * @returns The invoice positions data or null if not found
 */
function findInvoicePositionsData(formData: any): IInvoicePositionsData | null {
    if (!formData || typeof formData !== "object") {
        return null;
    }

    // Iterate through all fields in form_data
    for (const hashCode in formData) {
        if (formData.hasOwnProperty(hashCode)) {
            let fieldValue = formData[hashCode];

            // If fieldValue is a string, it might be stringified JSON
            if (typeof fieldValue === "string") {
                try {
                    const parsed = JSON.parse(fieldValue);
                    if (
                        parsed &&
                        typeof parsed === "object" &&
                        Array.isArray(parsed.invoicePositions)
                    ) {
                        return parsed as IInvoicePositionsData;
                    }
                } catch (e) {
                    // Not valid JSON, continue
                }
                continue;
            }

            // Check if this field contains invoicePositions as an object
            if (fieldValue && typeof fieldValue === "object") {
                if (Array.isArray(fieldValue.invoicePositions)) {
                    return fieldValue as IInvoicePositionsData;
                }

                // Handle stringified JSON inside invoicePositions property
                if (typeof fieldValue.invoicePositions === "string") {
                    try {
                        const parsed = JSON.parse(fieldValue.invoicePositions);
                        if (Array.isArray(parsed)) {
                            return { ...fieldValue, invoicePositions: parsed };
                        }
                    } catch (e) {
                        // Not valid JSON
                    }
                }
            }
        }
    }

    return null;
}

/**
 * Converts an invoice position to a door report item
 * @param position The invoice position from InvoicePositionsListField
 * @returns IDoorReportItem
 */
function convertToDoorReportItem(position: IInvoicePosition): IDoorReportItem {
    return {
        nr: position.nr,
        title: position.title || "",
        description: position.description || "",
        qty: typeof position.qty === "number" ? position.qty : 1,
        unit: position.unit || "repair",
        price: typeof position.price === "number" ? position.price : 0,
        inventoryItemId: position.inventory_item_id || null,
    };
}

/**
 * Groups invoice positions by door number based on description hints
 * @param positions Array of invoice positions
 * @returns Object with grouped doors and unassigned items
 */
function groupPositionsByDoor(
    positions: IInvoicePosition[]
): {
    doors: Map<number, IDoorReportItem[]>;
    unassigned: IDoorReportItem[];
} {
    const doors = new Map<number, IDoorReportItem[]>();
    const unassigned: IDoorReportItem[] = [];

    for (const position of positions) {
        const doorNumbers = extractDoorNumbers(position.description);
        const reportItem = convertToDoorReportItem(position);

        if (doorNumbers.length === 0) {
            // No door hints found, add to unassigned
            unassigned.push(reportItem);
        } else {
            // Add to each door group
            for (const doorNumber of doorNumbers) {
                if (!doors.has(doorNumber)) {
                    doors.set(doorNumber, []);
                }
                doors.get(doorNumber)!.push(reportItem);
            }
        }
    }

    return { doors, unassigned };
}

/**
 * Generates a door report from form entry data
 * @param formEntry The form entry object with id, name, entity, and form_data
 * @returns IDoorReport or null if no invoice positions found
 */
export function generateDoorReport(formEntry: any): IDoorReport | null {
    if (!formEntry || !formEntry.form_data) {
        return null;
    }

    const invoiceData = findInvoicePositionsData(formEntry.form_data);

    if (!invoiceData || !Array.isArray(invoiceData.invoicePositions)) {
        return null;
    }

    // Filter out empty positions
    const validPositions = invoiceData.invoicePositions.filter(
        (pos) => pos && (pos.title || pos.description)
    );

    if (validPositions.length === 0) {
        return null;
    }

    const { doors, unassigned } = groupPositionsByDoor(validPositions);

    // Convert map to sorted array
    const sortedDoorNumbers = Array.from(doors.keys()).sort((a, b) => a - b);
    const doorGroups: IDoorGroup[] = sortedDoorNumbers.map((doorNumber) => ({
        doorNumber,
        doorLabel: `Tür ${doorNumber}`,
        items: doors.get(doorNumber) || [],
    }));

    // Calculate total items
    const totalItems = validPositions.length;

    return {
        formEntryId: formEntry.id,
        formEntryName: formEntry.name || "",
        entityName: formEntry.entity?.name || formEntry.entity_name || "",
        doors: doorGroups,
        unassignedItems: unassigned,
        totalItems,
    };
}

/**
 * Checks if a form entry has invoice positions that could be grouped by door
 * @param formEntry The form entry to check
 * @returns boolean
 */
export function hasDoorReportData(formEntry: any): boolean {
    if (!formEntry || !formEntry.form_data) {
        return false;
    }

    const invoiceData = findInvoicePositionsData(formEntry.form_data);

    if (!invoiceData || !Array.isArray(invoiceData.invoicePositions)) {
        return false;
    }

    return invoiceData.invoicePositions.some((pos) => pos && (pos.title || pos.description));
}

/**
 * Extracts all unique door numbers from a form entry
 * @param formEntry The form entry to analyze
 * @returns Array of door numbers
 */
export function extractAllDoorNumbers(formEntry: any): number[] {
    if (!formEntry || !formEntry.form_data) {
        return [];
    }

    const invoiceData = findInvoicePositionsData(formEntry.form_data);

    if (!invoiceData || !Array.isArray(invoiceData.invoicePositions)) {
        return [];
    }

    const allDoorNumbers = new Set<number>();

    for (const position of invoiceData.invoicePositions) {
        if (position && position.description) {
            const doorNumbers = extractDoorNumbers(position.description);
            doorNumbers.forEach((num) => allDoorNumbers.add(num));
        }
    }

    return Array.from(allDoorNumbers).sort((a, b) => a - b);
}
